import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';

const MercatelyChat = ({ selectedConversation, onJsonClick }) => {
  const [mercatelyMessages, setMercatelyMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedJsonMessage, setSelectedJsonMessage] = useState(null);
  
  useEffect(() => {
    const fetchMercatelyConversation = async () => {
      if (!selectedConversation || !selectedConversation.id_simple) {
        setMercatelyMessages([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      setSelectedJsonMessage(null); // Reset selection on new conversation

      try {
        // 1. Parsear la información del vehículo para construir la ruta dinámica
        const vehicleInfo = parseVehicleInfo(selectedConversation.messages);
        if (!vehicleInfo) {
          throw new Error("No se pudo encontrar la información del vehículo en la conversación principal.");
        }
        const dynamicDocName = `envio_${vehicleInfo.marca}_${vehicleInfo.submarca}_${vehicleInfo.anio}`;

        // 2. Construir la ruta completa
        const path = `conversations/${selectedConversation.id}/datos_para_cotizacion/${dynamicDocName}/conversacion_wa`;
        console.log("Ruta de Mercately:", path);
        
        const mercatelyConvRef = collection(db, path); // This is a collection of documents
        const q = query(mercatelyConvRef, orderBy('updated_at', 'asc')); // Order documents by timestamp
        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
          setError("No se encontró la conversación de Mercately. La ruta puede ser incorrecta o no existen datos.");
          setMercatelyMessages([]);
        } else {
          // Each document inside 'conversacion_wa' has a 'mensajes' array.
          // We need to get all documents and then flatten their 'mensajes' arrays.
          const messagesData = querySnapshot.docs
            .flatMap(doc => doc.data().mensajes || [])
            .sort((a, b) => (a.timestamp?.seconds || 0) - (b.timestamp?.seconds || 0));
          setMercatelyMessages(messagesData);
        }
      } catch (e) {
        console.error("Error fetching Mercately conversation:", e);
        setError(`Error al cargar la conversación: ${e.message}`);
        setMercatelyMessages([]);
      } finally {
        setLoading(false);
      }
    };

    fetchMercatelyConversation();
  }, [selectedConversation]);

  const parseVehicleInfo = (messages) => {
    const validationMessage = messages.find(
      (m) => m.role === 'system' && m.content.startsWith('Proceso de validación exitoso para el auto')
    );
    if (validationMessage) {
      const vehicleRegex = /Proceso de validación exitoso para el auto ([\w\s-]+) ([\w\s-]+) (\d{4})/;
      const match = validationMessage.content.match(vehicleRegex);
      if (match) {
        return {
          marca: match[1].trim().replace(/\s/g, '_'),
          submarca: match[2].trim().replace(/\s/g, '_'),
          anio: match[3].trim(),
        };
      }
    }
    return null;
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    if (timestamp.toDate) {
      return new Date(timestamp.toDate()).toLocaleString();
    } else if (timestamp.seconds) {
      return new Date(timestamp.seconds * 1000).toLocaleString();
    } else if (typeof timestamp === 'string' || timestamp instanceof Date) {
      return new Date(timestamp).toLocaleString();
    } else {
      return 'Invalid date';
    }
  };

  if (loading) {
    return <div className="text-center text-gray-500">Cargando conversación de Mercately...</div>;
  }

  if (error) {
    return <div className="text-center text-red-500 p-4 bg-red-50 rounded-lg">{error}</div>;
  }

  if (mercatelyMessages.length === 0) {
    return <div className="text-center text-gray-500">No hay mensajes en esta conversación de Mercately.</div>;
  }

  return (
    <div className="flex-1 p-4 overflow-y-auto bg-gray-50" style={{ maxHeight: 'calc(78vh - 50px)' }}>
      <div className="flex flex-col space-y-4">
        {mercatelyMessages.map((message, idx) => {          
          const isJsonMessage = message.role === 'model' && typeof message.content === 'string' && message.content.startsWith('{"informacion_usuario"');
          const isSelected = selectedJsonMessage === message.content;

          return (
            <div key={idx}
              className={`p-3 rounded-lg max-w-xs ${
                isJsonMessage
                  ? 'bg-purple-500 text-white self-center'
                  : message.role === 'model'
                  ? 'text-white self-end bg-[#4790CF]'
                  : 'bg-gray-300 text-black self-start'
              } ${isJsonMessage ? 'cursor-pointer hover:bg-gray-400' : ''} ${isSelected ? 'border-2 border-green-500 shadow-lg' : ''}`}
              onClick={() => isJsonMessage && onJsonClick(message.content)}>
              
              {isJsonMessage ? "JSON Cotizacion WS" : 
                (message.content || '').split('\n').map((line, lineIdx) => (
                  <span key={lineIdx}>
                    {line}
                    <br />
                  </span>
                ))
              }

              {message.timestamp && (
                <div className={`text-xs mt-2 text-right ${message.role === 'model' ? 'text-white' : 'text-gray-500'}`}>
                  {formatTimestamp(message.timestamp)}
                </div>
              )}
            </div>
          );
        })}      
      </div>
    </div>
  );
};

export default MercatelyChat;